/**
 * src/modules/connect/lib/meetingSession.js
 *
 * Tab-scoped Meeting Participant Session Management.
 *
 * Stores the per-meeting participant credential (meeting-scoped JWT + participant identity)
 * returned by POST /connect/meetings/{meeting_id}/join-session.
 *
 * Guarantees:
 * 1. Isolated in sessionStorage (key: synexos_meeting_session_<meetingId>) and memory.
 * 2. Survives main-app logout, token refresh, and Account B login in the same browser/tab.
 * 3. Never touches or mutates global auth storage (localStorage.user, localStorage.access_token, etc.).
 * 4. Immutable meeting identity (host, participant_id, user_id, org_id) for the active session.
 */

import { decodeJwt, authService } from "../../../utils/auth";
import logger from "../../../utils/logger";

const SESSION_PREFIX = "synexos_meeting_session_";
const memorySessions = new Map();

export const getMeetingStorageKey = (meetingId) => `${SESSION_PREFIX}${meetingId}`;

/**
 * Read meeting session from sessionStorage with memory fallback.
 */
export function getMeetingSession(meetingId) {
  if (!meetingId) return null;
  const key = getMeetingStorageKey(meetingId);
  try {
    if (typeof sessionStorage !== "undefined") {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          return parsed;
        }
      }
    }
  } catch (err) {
    logger.warn("[MeetingSession] Failed reading sessionStorage:", err?.message || err);
  }
  return memorySessions.get(meetingId) || null;
}

/**
 * Check if the meeting session exists, matches meetingId/user/org, and is unexpired.
 */
export function isMeetingSessionValid(meetingId) {
  if (!meetingId) return false;
  const session = getMeetingSession(meetingId);
  const token = session?.meeting_token || session?.token;
  if (!token) return false;

  try {
    const payload = decodeJwt(token);
    if (!payload || typeof payload.exp !== "number") return false;

    // 1. Expiration validation
    const nowSec = Math.floor(Date.now() / 1000);
    if (payload.exp <= nowSec) return false;

    // 2. Meeting ID match validation
    const tokenMeetingId = payload.meeting_id || session.meeting_id || session.session?.meeting_id;
    if (tokenMeetingId && tokenMeetingId !== meetingId) return false;

    // 3. User & Org identity validation against current authenticated user (when logged in)
    const currentUser = authService.getUser();
    if (currentUser) {
      const currentUserId = currentUser.id || currentUser.user_id || currentUser._id;
      const currentOrgId = currentUser.org_id || currentUser.organization_id;

      const sessionUserId = session.session?.user_id || session.user_id || payload.user_id;
      if (currentUserId && sessionUserId && sessionUserId !== currentUserId) {
        return false;
      }

      const sessionOrgId = session.session?.organization_id || session.org_id || payload.org_id;
      if (currentOrgId && sessionOrgId && sessionOrgId !== currentOrgId) {
        return false;
      }
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Get meeting-scoped JWT token for a meeting (only if valid).
 */
export function getMeetingToken(meetingId) {
  if (!meetingId) return null;
  if (!isMeetingSessionValid(meetingId)) return null;
  const session = getMeetingSession(meetingId);
  return session?.meeting_token || session?.token || null;
}

/**
 * Get the immutable participant identity for the active meeting.
 */
export function getMeetingParticipant(meetingId) {
  const session = getMeetingSession(meetingId);
  return session?.session || session?.participant || null;
}

/**
 * Save or update meeting session in sessionStorage and in-memory mirror.
 */
export function saveMeetingSession(meetingId, data) {
  if (!meetingId || !data) return null;
  const key = getMeetingStorageKey(meetingId);
  const existing = getMeetingSession(meetingId) || {};
  const merged = {
    ...existing,
    ...data,
    meeting_id: meetingId,
    updated_at: Date.now(),
  };

  memorySessions.set(meetingId, merged);

  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(key, JSON.stringify(merged));
    }
  } catch (err) {
    logger.warn("[MeetingSession] Failed writing sessionStorage:", err?.message || err);
  }

  logger.info("[MeetingSession] Saved meeting session for meeting:", meetingId, {
    participant_id: merged.session?.participant_id,
    user_id: merged.session?.user_id,
    role: merged.session?.role,
    org_id: merged.session?.organization_id,
  });

  return merged;
}

/**
 * Clear meeting session (upon explicit leave or completed end of meeting).
 */
export function clearMeetingSession(meetingId) {
  if (!meetingId) return;
  const key = getMeetingStorageKey(meetingId);
  memorySessions.delete(meetingId);
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
  logger.info("[MeetingSession] Cleared meeting session for meeting:", meetingId);
}

export default {
  getMeetingSession,
  getMeetingToken,
  getMeetingParticipant,
  saveMeetingSession,
  clearMeetingSession,
  isMeetingSessionValid,
};
